import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';

const MONTH_NAMES_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];
const MONTH_ABBR_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const WEEKDAY_ABBR_ES = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];

const pad2 = (n) => String(n).padStart(2, '0');

// Native <input type="date">/type="month"> render their own text in
// whatever format the device/browser locale is set to -- there's no way to
// force MM/DD/YYYY on them from JS. This is a from-scratch picker so the
// displayed format is always MM/DD/YYYY (or MM/YYYY), independent of device
// locale, while still emitting the same "YYYY-MM-DD"/"YYYY-MM" value a
// native input would.
const DatePicker = ({ mode = 'date', value, onChange, placeholder, className }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const parsed = value
    ? (mode === 'date'
        ? { year: Number(value.slice(0, 4)), month: Number(value.slice(5, 7)) - 1, day: Number(value.slice(8, 10)) }
        : { year: Number(value.slice(0, 4)), month: Number(value.slice(5, 7)) - 1 })
    : null;

  const today = new Date();
  const [viewYear, setViewYear] = useState(parsed ? parsed.year : today.getFullYear());
  const [viewMonth, setViewMonth] = useState(parsed ? parsed.month : today.getMonth());

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const label = parsed
    ? mode === 'date'
      ? `${pad2(parsed.month + 1)}/${pad2(parsed.day)}/${parsed.year}`
      : `${pad2(parsed.month + 1)}/${parsed.year}`
    : (placeholder || (mode === 'date' ? 'mm/dd/yyyy' : 'mm/yyyy'));

  const selectDay = (day) => {
    onChange(`${viewYear}-${pad2(viewMonth + 1)}-${pad2(day)}`);
    setOpen(false);
  };

  const selectMonth = (monthIndex) => {
    onChange(`${viewYear}-${pad2(monthIndex + 1)}`);
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange('');
  };

  // Monday-first grid, matching the week-start convention used elsewhere
  // in the app (see the Monday-anchored week math in CashControlSystem).
  const renderDateGrid = () => {
    const firstWeekday = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells = [...Array(firstWeekday).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

    return (
      <>
        <div className="flex items-center justify-between mb-2">
          <button
            type="button"
            onClick={() => setViewMonth(m => { if (m === 0) { setViewYear(y => y - 1); return 11; } return m - 1; })}
            className="p-1 text-gray-400 hover:text-teal-300"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm font-semibold text-white">{MONTH_NAMES_ES[viewMonth]} {viewYear}</span>
          <button
            type="button"
            onClick={() => setViewMonth(m => { if (m === 11) { setViewYear(y => y + 1); return 0; } return m + 1; })}
            className="p-1 text-gray-400 hover:text-teal-300"
          >
            <ChevronRight size={16} />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {WEEKDAY_ABBR_ES.map(d => (
            <span key={d} className="text-center text-xs text-gray-500">{d}</span>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((day, i) => {
            if (day === null) return <span key={`blank-${i}`} />;
            const isSelected = parsed && parsed.year === viewYear && parsed.month === viewMonth && parsed.day === day;
            const isToday = today.getFullYear() === viewYear && today.getMonth() === viewMonth && today.getDate() === day;
            return (
              <button
                key={day}
                type="button"
                onClick={() => selectDay(day)}
                className={`text-xs py-1.5 rounded-lg transition-colors ${
                  isSelected
                    ? 'bg-teal-500 text-black font-semibold'
                    : isToday
                      ? 'border border-teal-400/60 text-teal-300'
                      : 'text-gray-300 hover:bg-teal-500/15'
                }`}
              >
                {day}
              </button>
            );
          })}
        </div>
      </>
    );
  };

  const renderMonthGrid = () => (
    <>
      <div className="flex items-center justify-between mb-2">
        <button type="button" onClick={() => setViewYear(y => y - 1)} className="p-1 text-gray-400 hover:text-teal-300">
          <ChevronLeft size={16} />
        </button>
        <span className="text-sm font-semibold text-white">{viewYear}</span>
        <button type="button" onClick={() => setViewYear(y => y + 1)} className="p-1 text-gray-400 hover:text-teal-300">
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-1">
        {MONTH_ABBR_ES.map((abbr, i) => {
          const isSelected = parsed && parsed.year === viewYear && parsed.month === i;
          return (
            <button
              key={abbr}
              type="button"
              onClick={() => selectMonth(i)}
              className={`text-xs py-2 rounded-lg transition-colors ${
                isSelected ? 'bg-teal-500 text-black font-semibold' : 'text-gray-300 hover:bg-teal-500/15'
              }`}
            >
              {abbr}
            </button>
          );
        })}
      </div>
    </>
  );

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`${className} flex items-center justify-between gap-2`}
      >
        <span className={value ? '' : 'text-gray-500'}>{label}</span>
        <span className="flex items-center gap-1 flex-shrink-0">
          {value && (
            <span onClick={clear} className="text-gray-500 hover:text-white cursor-pointer">
              <X size={14} />
            </span>
          )}
          <Calendar size={14} className="text-gray-500" />
        </span>
      </button>

      {open && (
        <div className="absolute z-20 mt-1 p-3 bg-gray-900 border border-teal-500/40 rounded-lg shadow-lg w-64">
          {mode === 'date' ? renderDateGrid() : renderMonthGrid()}
        </div>
      )}
    </div>
  );
};

export default DatePicker;
